import * as React from "react"
import { router } from "@inertiajs/react"
import {
  Card,
  CardHeader,
  CardTitle,
  CardDescription,
  CardContent,
  CardFooter,
} from "@/components/ui/Card"
import { Button } from "@/components/ui/Button"
import { Input } from "@/components/ui/Input"
import { Textarea } from "@/components/ui/Textarea"
import { Badge } from "@/components/ui/Badge"
import { Separator } from "@/components/ui/Separator"
import { admin_integrations_google_path, admin_integrations_google_test_path } from "@/lib/routes"

export interface GoogleState {
  connected: boolean
  enabled?: boolean
  sender_email?: string | null
  service_account?: string | null
  connected_by?: string | null
  connected_at?: string | null
}

// Google Workspace: task emails and calendar events. The key is pasted once
// by an admin; it is never sent back to the page.
export default function GoogleIntegrationCard({ google }: { google: GoogleState }) {
  const [key, setKey] = React.useState("")
  const [sender, setSender] = React.useState(google.sender_email ?? "")
  const [busy, setBusy] = React.useState(false)

  const visit = (opts: Parameters<typeof router.patch>[2] = {}) => ({
    preserveScroll: true,
    onStart: () => setBusy(true),
    onFinish: () => setBusy(false),
    ...opts,
  })

  function connect(e: React.FormEvent) {
    e.preventDefault()
    if (!key.trim() || !sender.trim()) return
    router.patch(
      admin_integrations_google_path(),
      { service_account_key: key.trim(), sender_email: sender.trim() },
      visit({ onSuccess: () => setKey("") }),
    )
  }

  const sendTest = () => router.post(admin_integrations_google_test_path(), {}, visit())
  const toggle = () =>
    router.patch(admin_integrations_google_path(), { is_enabled: !google.enabled }, visit())
  function disconnect() {
    if (!window.confirm("Disconnect Google? Atlas will stop sending task emails and calendar events.")) return
    router.delete(admin_integrations_google_path(), visit())
  }

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between gap-3">
          <div className="space-y-1.5">
            <CardTitle className="text-lg">Google Workspace</CardTitle>
            <CardDescription>
              Email people and add tasks to their Google Calendar when it is their turn.
            </CardDescription>
          </div>
          {google.connected ? (
            <Badge variant={google.enabled ? "success" : "warning"}>
              {google.enabled ? "Connected" : "Paused"}
            </Badge>
          ) : (
            <Badge variant="outline">Not connected</Badge>
          )}
        </div>
      </CardHeader>

      {google.connected ? (
        <>
          <CardContent className="space-y-2 text-sm">
            <Row label="Sends from" value={google.sender_email ?? "—"} />
            <Row label="Service account" value={google.service_account ?? "—"} />
            <Row
              label="Connected"
              value={[google.connected_by, formatDate(google.connected_at)].filter(Boolean).join(" · ") || "—"}
            />
          </CardContent>
          <Separator />
          <CardFooter className="flex flex-wrap gap-2 pt-6">
            <Button variant="outline" onClick={sendTest} disabled={busy}>
              Send test
            </Button>
            <Button variant="outline" onClick={toggle} disabled={busy}>
              {google.enabled ? "Pause" : "Resume"}
            </Button>
            <Button variant="ghost" onClick={disconnect} disabled={busy} className="ml-auto text-destructive">
              Disconnect
            </Button>
          </CardFooter>
        </>
      ) : (
        <CardContent>
          <form onSubmit={connect} className="space-y-3">
            <div className="space-y-1.5">
              <label className="text-sm font-medium text-foreground">Service account key (JSON)</label>
              <Textarea
                rows={5}
                autoComplete="off"
                value={key}
                onChange={(e) => setKey(e.target.value)}
                placeholder='{"type": "service_account", …}'
              />
              <p className="text-xs text-muted-foreground">
                A Google service account with domain-wide delegation for the scopes{" "}
                <code className="text-foreground">calendar.events</code> and{" "}
                <code className="text-foreground">gmail.send</code>.
              </p>
            </div>
            <div className="space-y-1.5">
              <label className="text-sm font-medium text-foreground">Send from</label>
              <Input
                placeholder="atlas@yourcompany.com"
                value={sender}
                onChange={(e) => setSender(e.target.value)}
              />
            </div>
            <Button type="submit" disabled={busy || !key.trim() || !sender.trim()}>
              Connect
            </Button>
          </form>
        </CardContent>
      )}
    </Card>
  )
}

function Row({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex justify-between gap-4">
      <span className="text-muted-foreground">{label}</span>
      <span className="min-w-0 break-all text-foreground text-right">{value}</span>
    </div>
  )
}

function formatDate(iso?: string | null): string {
  if (!iso) return ""
  const d = new Date(iso)
  return Number.isNaN(d.getTime()) ? "" : d.toLocaleDateString()
}
